import { twMerge } from 'tailwind-merge';

export type ButtonColour = 'primary' | 'secondary' | 'neutral' | 'info' | 'warning' | 'danger';
export type ButtonAppearance = 'solid' | 'outline' | 'ghost';
/** Kept for existing call sites. New code uses a colour variant with `appearance` instead. */
export type LegacyButtonVariant = 'outline' | 'ghost' | 'clear';
export type ButtonVariant = ButtonColour | LegacyButtonVariant;
export type ButtonSize = 'none' | 'xs' | 'sm' | 'md';

const SIZE_CLASSES: Record<ButtonSize, string> = {
    none: '',
    xs: 'px-3 py-2 text-xs',
    sm: 'px-4 py-3 text-sm',
    md: 'px-5 py-3.5 text-base',
};

const COLOUR_CLASSES: Record<ButtonColour, Record<ButtonAppearance, string>> = {
    primary: {
        solid: 'bg-mp-primary text-mp-primary-foreground hover:bg-hover-mp-primary',
        outline: 'bg-mp-surface text-mp-link ring-1 ring-inset ring-mp-primary hover:bg-mp-primary-content',
        ghost: 'bg-transparent text-mp-link hover:bg-mp-primary-content',
    },
    secondary: {
        solid: 'bg-mp-secondary-content text-mp-secondary hover:bg-hover-mp-secondary-content',
        outline: 'bg-mp-surface text-mp-secondary ring-1 ring-inset ring-mp-secondary hover:bg-mp-secondary-content',
        ghost: 'bg-transparent text-mp-secondary hover:bg-mp-secondary-content',
    },
    neutral: {
        solid: 'bg-mp-border text-mp-foreground hover:bg-mp-input',
        outline: 'bg-mp-surface text-mp-foreground ring-1 ring-inset ring-mp-input hover:bg-hover-mp-surface',
        ghost: 'bg-transparent text-mp-foreground hover:bg-mp-border',
    },
    info: {
        solid: 'bg-mp-info text-mp-info-content hover:bg-hover-mp-info',
        outline: 'bg-mp-surface text-mp-info ring-1 ring-inset ring-mp-info hover:bg-mp-info-content',
        ghost: 'bg-transparent text-mp-info hover:bg-mp-info-content',
    },
    warning: {
        solid: 'bg-mp-warning-solid text-mp-warning-solid-foreground hover:bg-hover-mp-warning-solid',
        outline: 'bg-mp-surface text-mp-warning ring-1 ring-inset ring-mp-warning hover:bg-mp-warning-content',
        ghost: 'bg-transparent text-mp-warning hover:bg-mp-warning-content',
    },
    danger: {
        solid: 'bg-mp-error text-mp-error-content hover:bg-hover-mp-error',
        outline: 'bg-mp-surface text-mp-error ring-1 ring-inset ring-mp-error hover:bg-mp-error-content',
        ghost: 'bg-transparent text-mp-error hover:bg-mp-error-content',
    },
};

const LEGACY_VARIANT_CLASSES: Record<LegacyButtonVariant, string> = {
    outline: COLOUR_CLASSES.neutral.outline,
    ghost: COLOUR_CLASSES.neutral.ghost,
    clear: '',
};

const isLegacyVariant = (variant: ButtonVariant): variant is LegacyButtonVariant => variant in LEGACY_VARIANT_CLASSES;

export const variantClasses = (variant: ButtonVariant, appearance: ButtonAppearance): string =>
    isLegacyVariant(variant) ? LEGACY_VARIANT_CLASSES[variant] : COLOUR_CLASSES[variant][appearance];

interface ButtonClassOptions {
    variant?: ButtonVariant;
    /** Ignored by the legacy `outline`, `ghost` and `clear` variants, which fix their own style. */
    appearance?: ButtonAppearance;
    size?: ButtonSize;
    rounded?: boolean;
    className?: string;
}

export function buttonClassName({
    variant = 'primary',
    appearance = 'solid',
    size = 'md',
    rounded = true,
    className = '',
}: ButtonClassOptions = {}): string {
    const roundedClass = rounded ? 'rounded-lg' : 'rounded-none';
    return twMerge(
        'inline-flex items-center justify-center gap-2 transition',
        roundedClass,
        SIZE_CLASSES[size],
        variantClasses(variant, appearance),
        className,
    );
}
